// The loading screen (markup in legacy-scaffold.html, styles in styles.css): spinner,
// a progress bar + MB count driven by real bytes received, and a small three-line
// ticker of real data pulled from the snapshot as it unpacks (boot-log.ts) — one line
// each for players, fixtures and stats.
//
// Each line has its own feed: fresh items as they arrive, and once its section of the
// file has gone by, it keeps cycling through what it's already seen (the file is in
// section order — fixtures come first, players up to ~40%, stats throughout). Drawing
// is one textContent write per line per frame, so it can't slow the download down. It
// runs until the overlay lifts (pausing by itself while the snapshot runs, ~1s).

import { extractFixtures, extractPlayers, extractStats, type TickerItem } from "./boot-log.js";

export interface BootScreen {
  progress(loaded: number, total: number): void;
  unpacked(chunk: Uint8Array): void;
  /** Live mode: no snapshot download, so the byte bar and the ticker (both fed by it)
   *  have nothing to show — hide them rather than leave an empty bar on screen. */
  noDownload(): void;
  /** The snapshot is fully unpacked and about to run. Resolves once that's painted. */
  startingUp(): Promise<void>;
  /** Everything's loaded: fade the overlay out. */
  ready(): void;
  failed(): void;
}

// Caps: fresh items are only worth keeping if they'll be shown soon; the pool only
// needs enough variety to cycle through without visibly repeating.
const MAX_FRESH = 30;
const MAX_POOL = 400;

// A fresh item whose left text was among the last few added is dropped — later in
// the file the same few event stats repeat, and the pool is more varied than that.
const RECENT = 50;

/** One ticker line's data: shows fresh items first, then cycles the pool. */
function feed() {
  const fresh: TickerItem[] = [];
  const pool: TickerItem[] = [];
  const recent: string[] = [];
  let at = 0;
  return {
    add(incoming: TickerItem[]) {
      const items = incoming.filter((item) => {
        if (recent.includes(item.left)) return false;
        recent.push(item.left);
        if (recent.length > RECENT) recent.shift();
        return true;
      });
      fresh.push(...items);
      if (fresh.length > MAX_FRESH) fresh.splice(0, fresh.length - MAX_FRESH);
      for (const item of items) if (pool.length < MAX_POOL) pool.push(item);
    },
    next(): TickerItem | undefined {
      return fresh.shift() ?? (pool.length ? pool[at++ % pool.length] : undefined);
    },
  };
}

const mb = (n: number) => (n / 1e6).toFixed(1);
const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));

export function createBootScreen(root: HTMLElement): BootScreen {
  const text = root.querySelector<HTMLElement>(".loading-text");
  const bar = root.querySelector<HTMLElement>(".loading-progress");
  const label = root.querySelector<HTMLElement>(".loading-progress-label");
  const rows = [...root.querySelectorAll<HTMLElement>(".loading-ticker > div")].map((row) => ({
    left: row.querySelector<HTMLElement>(".lt-key"),
    right: row.querySelector<HTMLElement>(".lt-val"),
  }));
  if (!text || !bar || !label || rows.length !== 3 || rows.some((r) => !r.left || !r.right)) {
    throw new Error("Loading screen markup is missing (legacy-scaffold.html)");
  }
  const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const players = feed();
  const fixtures = feed();
  const stats = feed();
  const feeds = [players, fixtures, stats];
  const decoder = new TextDecoder();
  let running = true;

  // Each line gets a new item every frame (~60/s) — "data flashing past".
  function tick() {
    if (!running) return;
    feeds.forEach((f, i) => {
      const item = f.next();
      if (!item) return;
      rows[i]!.left!.textContent = item.left;
      rows[i]!.right!.textContent = item.right;
    });
    requestAnimationFrame(tick);
  }
  if (!calm) requestAnimationFrame(tick);

  return {
    progress(loaded, total) {
      const pct = Math.min(100, Math.round((loaded / total) * 100));
      bar.style.setProperty("--progress", `${pct}%`);
      bar.setAttribute("aria-valuenow", String(pct));
      label.textContent = `${mb(loaded)} / ${mb(total)} MB`;
    },

    unpacked(chunk) {
      if (calm) return;
      // `stream: true` keeps a character split across two chunks intact. A record split
      // across chunks is simply missed — there are plenty of others.
      const t = decoder.decode(chunk, { stream: true });
      players.add(extractPlayers(t));
      fixtures.add(extractFixtures(t));
      stats.add(extractStats(t));
    },

    noDownload() {
      running = false;
      for (const el of [bar, label, root.querySelector<HTMLElement>(".loading-ticker")]) {
        if (el) el.style.display = "none";
      }
    },

    // The ticker keeps going: it pauses by itself while the snapshot runs (the main
    // thread is busy), then carries on through the rest of start-up.
    async startingUp() {
      text.textContent = "Starting up…";
      // Two frames so this is on screen before the script run blocks the main thread.
      await nextFrame();
      await nextFrame();
    },

    ready() {
      running = false;
      root.classList.add("loaded");
    },

    failed() {
      running = false;
      text.textContent = "Couldn't load the data — please refresh.";
    },
  };
}
